import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play } from "lucide-react";
import { getCallFeatures, listCallVoices, putCallFeature, testCallCalendar } from "../lib/api";

const ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "Europe/Berlin", "Australia/Sydney", "Australia/Melbourne"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? " am" : " pm"}`;
const errText = (e, fallback) => e?.response?.data?.detail || fallback;

function Switch({ on, busy, onClick, label }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onClick} disabled={busy} className={`switch ${on ? "on" : ""}`} data-testid={`switch-${label}`}><span /></button>;
}

function Card({ title, blurb, feature, onToggle, onSave, busy, children, extra }) {
  return <div className="panel feature-card" data-testid={`feature-${title}`}>
    <div className="feature-head">
      <Switch on={feature.enabled} busy={busy} onClick={onToggle} label={title} />
      <div><b>{title}</b> <span className={`badge ${feature.enabled ? "badge-green" : ""}`}>{feature.enabled ? "On" : "Off"}</span><p>{blurb}</p></div>
      {busy && <Loader2 size={16} className="spin" />}
    </div>
    {!feature.enabled && feature.missing?.length > 0 && <p className="feature-missing">To switch on: {feature.missing.join(" · ")}</p>}
    <div className="feature-body">{children}</div>
    <div className="feature-foot"><button className="outline-button" onClick={onSave} disabled={busy}>Save settings</button>{extra}</div>
  </div>;
}

const Field = ({ label, hint, children }) => <label className="feature-field">{label}{children}{hint && <small>{hint}</small>}</label>;

export function FeaturesPanel() {
  const [data, setData] = useState(null);
  const [lt, setLt] = useState(null);
  const [cal, setCal] = useState(null);
  const [busy, setBusy] = useState("");
  const [slots, setSlots] = useState(null);

  const load = () => getCallFeatures().then(d => { setData(d); setLt(d.features.live_transfer); setCal({ ...d.features.calendar, api_key: "" }); })
    .catch(() => toast.error("Could not load call features"));
  useEffect(() => { load(); }, []);
  if (!data || !lt || !cal) return null;
  const zones = (z) => (ZONES.includes(z) ? ZONES : [z, ...ZONES]);

  const save = async (name, form, enabled) => {
    setBusy(name);
    try {
      const { missing, api_key_set, ...fields } = form;   // eslint-disable-line no-unused-vars
      const body = { ...fields, ...(enabled === undefined ? {} : { enabled }) };
      if (!body.api_key) delete body.api_key;
      await putCallFeature(name, body);
      toast.success(enabled === true ? "Switched on and applied to the live agent" : enabled === false ? "Switched off" : "Saved");
      await load();
    } catch (e) { toast.error(errText(e, "Could not save")); await load(); } finally { setBusy(""); }
  };

  return <div className="feature-list">
    <Card title="Live transfer" blurb="When someone is keen and asks for a person, the agent hands the call to your phone. Only offered during your hours."
      feature={lt} busy={busy === "live_transfer"} onToggle={() => save("live_transfer", lt, !lt.enabled)} onSave={() => save("live_transfer", lt)}>
      <Field label="TRANSFER TO (YOUR PHONE, WITH COUNTRY CODE)"><input value={lt.number} onChange={e => setLt({ ...lt, number: e.target.value })} placeholder="+14155550100" data-testid="transfer-number" /></Field>
      <div className="feature-three">
        <Field label="AVAILABLE FROM"><select value={lt.start} onChange={e => setLt({ ...lt, start: Number(e.target.value) })}>{HOURS.map(h => <option key={h} value={h}>{hourLabel(h)}</option>)}</select></Field>
        <Field label="UNTIL"><select value={lt.end} onChange={e => setLt({ ...lt, end: Number(e.target.value) })}>{HOURS.map(h => <option key={h} value={h}>{hourLabel(h)}</option>)}</select></Field>
        <Field label="TIME ZONE"><select value={lt.timezone} onChange={e => setLt({ ...lt, timezone: e.target.value })}>{zones(lt.timezone).map(z => <option key={z}>{z}</option>)}</select></Field>
      </div>
      <label className="inline-check"><input type="checkbox" checked={lt.weekdays_only} onChange={e => setLt({ ...lt, weekdays_only: e.target.checked })} /> Weekdays only</label>
      <Field label="HOW TO TRANSFER" hint="“Tell me who's calling” plays a short spoken summary to you before connecting (works on Twilio numbers).">
        <select value={lt.mode} onChange={e => setLt({ ...lt, mode: e.target.value })}><option value="blind">Connect straight away</option><option value="warm">Tell me who's calling first</option></select></Field>
      <Field label="WHAT THE AGENT SAYS (OPTIONAL)"><input value={lt.message} onChange={e => setLt({ ...lt, message: e.target.value })} placeholder="Great, let me connect you now. One moment." /></Field>
    </Card>

    <Card title="Calendar booking" blurb="The agent reads your real free times and books one, instead of only taking a preferred time. Uses Cal.com (free) connected to your calendar."
      feature={cal} busy={busy === "calendar"} onToggle={() => save("calendar", cal, !cal.enabled)} onSave={() => save("calendar", cal)}
      extra={<button className="outline-button" onClick={async () => { setBusy("test"); try { setSlots((await testCallCalendar()).slots); } catch (e) { setSlots(null); toast.error(errText(e, "Test failed")); } finally { setBusy(""); } }} disabled={!!busy}>Test calendar</button>}>
      <Field label="CAL.COM API KEY" hint={cal.api_key_set ? "A key is saved. Leave blank to keep it." : "In Cal.com: Settings → Developer → API keys."}>
        <input type="password" autoComplete="off" value={cal.api_key} onChange={e => setCal({ ...cal, api_key: e.target.value })} placeholder={cal.api_key_set ? "Saved" : "cal_live_…"} data-testid="cal-key" /></Field>
      <div className="feature-three">
        <Field label="EVENT TYPE ID" hint="The number in the event type's address in Cal.com."><input value={cal.event_type_id} onChange={e => setCal({ ...cal, event_type_id: e.target.value })} data-testid="cal-event" /></Field>
        <Field label="TIME ZONE"><select value={cal.timezone} onChange={e => setCal({ ...cal, timezone: e.target.value })}>{zones(cal.timezone).map(z => <option key={z}>{z}</option>)}</select></Field>
        <Field label="LOOK AHEAD (DAYS)"><input type="number" min={1} max={30} value={cal.days_ahead} onChange={e => setCal({ ...cal, days_ahead: Number(e.target.value) })} /></Field>
      </div>
      {slots && <div className="feature-slots"><b>Next open times the agent would offer:</b><ul>{slots.length ? slots.map(s => <li key={s}>{s}</li>) : <li>None found in the next few days.</li>}</ul></div>}
    </Card>
  </div>;
}

// The voices a provider offers, with a spoken preview. Shown only when the provider has a library (Vapi).
export function VoicePicker({ value, onChange }) {
  const [voices, setVoices] = useState(null);
  const [filter, setFilter] = useState("all");
  const audio = useRef(null);
  useEffect(() => { listCallVoices().then(setVoices).catch(() => setVoices([])); }, []);
  if (!voices || voices.length === 0) return null;
  const shown = voices.filter(v => filter === "all" || v.gender === filter);
  const play = (url) => { if (!url) return; audio.current.src = url; audio.current.play(); };
  return <div className="voice-picker" data-testid="voice-picker">
    <div className="voice-filter"><span>VOICE</span>{["all", "male", "female"].map(g => <button type="button" key={g} className={filter === g ? "primary-button" : "outline-button"} onClick={() => setFilter(g)}>{g}</button>)}</div>
    <div className="voice-list">{shown.map(v => <label key={v.id} className={value === v.id ? "on" : ""}>
      <input type="radio" name="voice" checked={value === v.id} onChange={() => onChange(v.id)} />
      <b>{v.id}</b><small>{[v.gender, v.accent, v.description].filter(Boolean).join(" · ")}</small>
      {v.preview_url && <button type="button" className="outline-button" onClick={(e) => { e.preventDefault(); play(v.preview_url); }}><Play size={11} /> Preview</button>}
    </label>)}</div>
    <audio ref={audio} />
  </div>;
}
